import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TablePaginator } from "@/components/TablePaginator";
import { useToast } from "@/hooks/use-toast";
import { api } from "@/lib/api";
import { Edit, Search, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { ApiResponse, Convenios, ConveniosListProps, PaginationInfo } from "./interface/Convenios.interface";

const ConveniosList = ({ onEdit, onDelete, refreshTrigger }: ConveniosListProps) => {
  const [convenios, setConvenios] = useState<Convenios[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const { toast } = useToast();

  const fetchConvenios = async (page: number = 1, search?: string) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "10",
        ...(search && { search })
      });

      const response: { result: ApiResponse } = await api.get(`/api/convenios?${params}`);
      
      if (response.result && response.result.data) {
        setConvenios(response.result.data);
        setPagination(response.result.pagination);
        setCurrentPage(page);
      } else {
        // Fallback para estrutura antiga (sem paginação)
        setConvenios(Array.isArray(response.result) ? response.result : []);
        setPagination(null);
        setCurrentPage(1);
      }
    } catch (error) {
      console.error("Erro ao carregar convênios:", error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os convênios",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConvenios(1);
  }, [refreshTrigger]);

  // Debounce para busca
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm !== "") {
        fetchConvenios(1, searchTerm);
      } else {
        fetchConvenios(1);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const handlePageChange = (page: number) => {
    fetchConvenios(page, searchTerm || undefined);
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center h-32">
          <div className="text-muted-foreground">Carregando convênios...</div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por descrição ou ANS..."
            className="pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Descrição</TableHead>
              <TableHead>ANS</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {convenios.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground">
                  Nenhum convênio encontrado
                </TableCell>
              </TableRow>
            ) : (
              convenios.map((convenio) => (
                <TableRow key={convenio.id}>
                  <TableCell className="font-medium">{convenio.descricao}</TableCell>
                  <TableCell>{convenio.ans || '-'}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      convenio.ativo
                        ? 'bg-success/10 text-success'
                        : 'bg-muted text-muted-foreground'
                    }`}>
                      {convenio.ativo ? 'Ativo' : 'Inativo'}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="icon" onClick={() => onEdit(convenio)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => onDelete(convenio.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {pagination && convenios.length > 0 && (
        <TablePaginator
          pagination={pagination}
          onPageChange={handlePageChange}
        />
      )}
    </Card>
  );
};

export default ConveniosList;

